import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';
import { AUTH_MODE } from '@/config';

const AuthContext = createContext(null);

/**
 * Decode the payload segment of a JWT without verifying signature.
 * Returns parsed JSON claims or null on failure.
 */
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('via_user'));
    } catch {
      return null;
    }
  });
  const [accessibleRoutes, setAccessibleRoutes] = useState([]);
  const [lastPermissionFetch, setLastPermissionFetch] = useState(null);
  const [isCommitteeMember, setIsCommitteeMember] = useState(false);

  const fetchPermissions = useCallback(async () => {
    if (!user) return;
    try {
      const [permRes, memberRes] = await Promise.all([
        api.get('/permissions/me'),
        api.get('/committee/membership/me').catch(() => ({ data: { is_member: false } })),
      ]);
      setAccessibleRoutes(permRes.data.accessible_routes || []);
      setIsCommitteeMember(memberRes.data?.is_member || false);
      setLastPermissionFetch(Date.now());
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  }, [user]);

  const login = useCallback(async (email, password) => {
    const payload = password ? { email, password } : { email };
    const endpoint = password ? '/auth/v2/login' : '/auth/login';
    const { data } = await api.post(endpoint, payload);

    // Store token and decode claims
    if (data.token) {
      sessionStorage.setItem('via_token', data.token);
      const claims = decodeJwtPayload(data.token);
      // Merge server user data with token claims for completeness
      const userData = data.user || {};
      if (claims) {
        userData.sub = claims.sub;
        userData.tokenExp = claims.exp;
      }
      sessionStorage.setItem('via_user', JSON.stringify(userData));
      setUser(userData);
    } else {
      sessionStorage.setItem('via_user', JSON.stringify(data.user));
      setUser(data.user);
    }

    // Fetch permissions immediately after login
    try {
      const [permRes, memberRes] = await Promise.all([
        api.get('/permissions/me'),
        api.get('/committee/membership/me').catch(() => ({ data: { is_member: false } })),
      ]);
      setAccessibleRoutes(permRes.data.accessible_routes || []);
      setIsCommitteeMember(memberRes.data?.is_member || false);
      setLastPermissionFetch(Date.now());
    } catch {
      // Permissions will be fetched by polling fallback
    }
    return data.user;
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('via_user');
    sessionStorage.removeItem('via_token');
    localStorage.removeItem('agp_user');
    setUser(null);
    setAccessibleRoutes([]);
    setLastPermissionFetch(null);
  }, []);

  // Token expiry check — every 60s, redirect to login if token expires within 5 minutes
  useEffect(() => {
    if (AUTH_MODE === 'demo') return;

    const checkTokenExpiry = () => {
      const token = sessionStorage.getItem('via_token');
      if (!token) return;

      const claims = decodeJwtPayload(token);
      if (!claims || !claims.exp) return;

      const nowSec = Math.floor(Date.now() / 1000);
      const bufferSec = 5 * 60; // 5 minutes
      if (claims.exp - nowSec <= bufferSec) {
        // Token expired or about to expire — force re-login
        sessionStorage.removeItem('via_user');
        sessionStorage.removeItem('via_token');
        setUser(null);
        window.location.href = '/login';
      }
    };

    // Check immediately on mount
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Polling every 120s as fallback (Requirement 9.6)
  useEffect(() => {
    if (!user) return;

    // Initial fetch on mount (handles page refresh)
    fetchPermissions();

    const interval = setInterval(fetchPermissions, 120_000);
    return () => clearInterval(interval);
  }, [user, fetchPermissions]);

  const tenant = user?.tenant_id || null;

  const hasAccess = useCallback(
    (path) => {
      // Exact match
      if (accessibleRoutes.includes(path)) return true;
      // Prefix match: if user has /toolkit, they can access /toolkit/prompts
      if (accessibleRoutes.some((route) => path.startsWith(route + '/'))) return true;
      // Dynamic route match: /iniciativas/:id matches /iniciativas/abc-123
      return accessibleRoutes.some((route) => {
        if (!route.includes(':')) return false;
        const routeSegments = route.split('/');
        const pathSegments = path.split('/');
        if (routeSegments.length !== pathSegments.length) return false;
        return routeSegments.every((seg, i) => seg.startsWith(':') || seg === pathSegments[i]);
      });
    },
    [accessibleRoutes],
  );

  const role = user?.role;
  const can = {
    edit: role === 'admin' || role === 'evaluador',
    delete: role === 'admin' || role === 'evaluador',
    vote: isCommitteeMember || role === 'admin',
    admin: role === 'admin',
    tasks: role !== 'lector',
    applications: role === 'admin' || role === 'evaluador' || role === 'comite',
    databases: role === 'admin' || role === 'evaluador',
    manageApplications: role === 'admin',
    reviewAccess: role === 'admin',
    requestAccess: role === 'admin',
    observability: role === 'admin' || role === 'evaluador',
  };

  return (
    <AuthContext.Provider
      value={{ user, login, logout, can, accessibleRoutes, tenant, hasAccess, lastPermissionFetch, fetchPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
